import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import { makeStyles } from "@material-ui/core/styles";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Box from "@material-ui/core/Box";
import {
	Title,
	SimpleForm,
	required,
	NumberInput,
	minValue,
	maxValue,
	ArrayInput,
	SimpleFormIterator,
	TextInput,
	useNotify,
	SelectInput,
	Button,
} from "react-admin";
import type { GetPricingModelQuery } from "~/api/get-pricing-model-query.generated.ts";
import getPricingModelQuery from "~/api/get-pricing-model-query.graphql";
import removeTypenames from "~/utils/remove-typenames.ts";
import type { UpdatePricingModelMutationVariables } from "~/api/update-pricing-model-mutation.ts";
import updatePricingModelMutation from "~/api/update-pricing-model-mutation.ts";
import getPricingReportQuery from "~/api/get-pricing-report-query.graphql";

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(1),
		width: "100%",
	},
	form: {
		width: "100%",
	},
	section: {
		marginBottom: theme.spacing(1),
	},
	sectionTitle: {
		marginBottom: theme.spacing(1),
		fontWeight: 500,
	},
	tabs: {
		borderBottom: `1px solid ${theme.palette.divider}`,
		marginBottom: theme.spacing(1),
		backgroundColor: theme.palette.background.paper,
		width: "100%",
	},
	tabPanel: {
		padding: theme.spacing(1, 0),
		width: "100%",
	},
	tab: {
		minWidth: 100,
	},
	tabContainer: {
		width: "100%",
	},
	tabContent: {
		padding: theme.spacing(1),
		width: "100%",
	},
	toolbar: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: theme.spacing(2, 0),
		borderTop: `1px solid ${theme.palette.divider}`,
		marginTop: theme.spacing(2),
	},
}));

type TabPanelProps = {
	children?: React.ReactNode;
	index: number;
	value: number;
};

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;
	const classes = useStyles();

	return (
		<div
			role="tabpanel"
			id={`pricing-tabpanel-${index}`}
			aria-labelledby={`pricing-tab-${index}`}
			className={classes.tabPanel}
			style={{ display: value === index ? "block" : "none" }}
			{...other}
		>
			<Box className={classes.tabContent}>{children}</Box>
		</div>
	);
}

function downloadCSV(data: any[], filename: string) {
	const headers = [
		"Baseplate Size",
		"Number of Baseplates",
		"Non-Premium Price",
		"Premium Price",
	];
	const csvContent = [
		headers.join(","),
		...data.map((item) =>
			[
				item.baseplateSize,
				item.numberOfBaseplates,
				item.nonPremiumPrice.toFixed(2),
				item.premiumPrice.toFixed(2),
			].join(","),
		),
	].join("\n");

	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
	const link = document.createElement("a");
	const url = URL.createObjectURL(blob);
	link.setAttribute("href", url);
	link.setAttribute("download", filename);
	link.style.visibility = "hidden";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

function EditPricingModel() {
	const classes = useStyles();
	const notify = useNotify();
	const [tabValue, setTabValue] = useState(0);

	const { data: pricingData } =
		useQuery<GetPricingModelQuery>(getPricingModelQuery);
	const { data: pricingReport, refetch: refetchPricingReport } = useQuery(
		getPricingReportQuery,
	);

	const [savePricingModel, savePricingModelResult] = useMutation<
		unknown,
		UpdatePricingModelMutationVariables
	>(updatePricingModelMutation, {
		onCompleted() {
			notify("Pricing model updated", "info");
			void refetchPricingReport();
		},
		onError(e) {
			notify(e.toString(), { type: "error" });
		},
	});

	const onSave = async (variables: any) => {
		await savePricingModel({
			variables: {
				...variables,
				discountBrackets: variables.discountBrackets || [],
				priceOverrides: (variables.priceOverrides || []).map(
					(override: any) => ({
						...override,
						amount: override.amount.toString(),
					}),
				),
			},
		});
	};

	const handleExportCSV = () => {
		if (pricingReport?.pricingReport) {
			downloadCSV(pricingReport.pricingReport, "pricing-report.csv");
		}
	};

	const pricingModel = (() => {
		if (!pricingData) {
			return undefined;
		}

		return {
			...pricingData.pricingModel,
			basePlateSizePrices: pricingData.pricingModel.basePlateSizePrices.map(
				(sizePrice) => ({
					size: sizePrice.size,
					priceDollars: parseFloat(sizePrice.price.amount),
				}),
			),
		};
	})();

	const baseplateSizeChoices =
		pricingModel?.basePlateSizePrices.map((sizePrice) => ({
			id: sizePrice.size,
			name: `${sizePrice.size}x${sizePrice.size} Baseplate`,
		})) ?? [];

	const handleTabChange = (_: React.ChangeEvent<unknown>, newValue: number) => {
		setTabValue(newValue);
	};

	return (
		<div className={classes.root}>
			<Title title="Pricing Model" />
			{pricingModel ? (
				<SimpleForm
					save={onSave}
					initialValues={removeTypenames(pricingModel)}
					saving={savePricingModelResult.loading}
					className={classes.form}
					keepDirtyOnReinitialize
					sanitizeEmptyValues={false}
				>
					<Button
						variant="contained"
						color="primary"
						onClick={handleExportCSV}
						disabled={
							!pricingReport?.pricingReport || savePricingModelResult.loading
						}
						style={{
							marginTop: "16px",
							marginLeft: "auto",
							display: "block",
							marginBottom: "10px",
						}}
					>
						<span style={{ fontSize: "0.875rem" }}>
							Export Pricing Report (CSV)
						</span>
					</Button>
					<div className={classes.tabContainer}>
						<Tabs
							value={tabValue}
							onChange={handleTabChange}
							aria-label="pricing model tabs"
							className={classes.tabs}
							indicatorColor="primary"
							textColor="primary"
							variant="fullWidth"
							centered
						>
							<Tab label="Color Premium" className={classes.tab} />
							<Tab label="Baseplate Prices" className={classes.tab} />
							<Tab label="Discount Tiers" className={classes.tab} />
							<Tab label="Price Overrides" className={classes.tab} />
						</Tabs>

						<TabPanel value={tabValue} index={0}>
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "16px",
								}}
							>
								<NumberInput
									source="numColoursPremiumThreshold"
									validate={[required(), minValue(1)]}
									step={1}
									min={1}
									helperText="Designs with number of colours above this will incur an extra cost"
								/>
								<NumberInput
									source="numColoursPremium"
									helperText="as a ratio, e.g. 0.05 = 5%"
									validate={[required(), minValue(0), maxValue(1)]}
									step={0.01}
									min={0}
									max={1}
								/>
							</div>
						</TabPanel>

						<TabPanel value={tabValue} index={1}>
							<ArrayInput source="basePlateSizePrices">
								<SimpleFormIterator disableAdd disableRemove>
									<TextInput source="size" label="Baseplate size" disabled />
									<NumberInput
										source="priceDollars"
										label="Price"
										helperText="per plate"
										validate={[required(), minValue(0)]}
										step={0.01}
										min={0}
									/>
								</SimpleFormIterator>
							</ArrayInput>
						</TabPanel>

						<TabPanel value={tabValue} index={2}>
							<ArrayInput source="discountBrackets">
								<SimpleFormIterator>
									<SelectInput
										source="baseplateSize"
										label="Baseplate Size"
										choices={baseplateSizeChoices}
										validate={[required()]}
									/>
									<NumberInput
										source="minimumQuantity"
										label="Minimum Quantity"
										validate={[required(), minValue(1)]}
										step={1}
										min={1}
									/>
									<NumberInput
										source="discount"
										label="Discount"
										helperText="as a ratio, e.g. 0.05 = 5%"
										validate={[required(), minValue(0), maxValue(1)]}
										step={0.01}
										min={0}
										max={1}
									/>
								</SimpleFormIterator>
							</ArrayInput>
						</TabPanel>

						<TabPanel value={tabValue} index={3}>
							<ArrayInput source="priceOverrides">
								<SimpleFormIterator>
									<SelectInput
										source="baseplateSize"
										label="Baseplate Size"
										choices={baseplateSizeChoices}
										validate={[required()]}
									/>
									<NumberInput
										source="quantity"
										label="Quantity"
										validate={[required(), minValue(1)]}
										step={1}
										min={1}
									/>
									<NumberInput
										source="amount"
										label="Price Override"
										helperText="in dollars"
										validate={[required(), minValue(0)]}
										step={0.01}
										min={0}
									/>
								</SimpleFormIterator>
							</ArrayInput>
						</TabPanel>
					</div>
				</SimpleForm>
			) : (
				<div>Loading</div>
			)}
		</div>
	);
}

export { EditPricingModel };
